/**
 * licenseAuth.js — Licence par appareil (ECDSA P-256, challenge-response)
 *
 * Côté client uniquement :
 *  - Génère une paire de clés ECDSA P-256 la première fois.
 *  - Stocke la clé PRIVÉE en JWK dans localStorage (jamais transmise au serveur).
 *  - Exporte la clé PUBLIQUE en SPKI PEM pour l'enregistrer côté serveur.
 *  - Signe les challenges émis par le serveur pour prouver la possession de la clé privée.
 *
 * Le serveur vérifie la signature avec la clé publique stockée et contrôle l'état
 * de la licence (active/suspendue/révoquée) avant d'autoriser l'accès.
 */

const PRIVATE_KEY_STORAGE_KEY = 'gs_license_private_jwk';
const PUBLIC_KEY_STORAGE_KEY = 'gs_license_public_pem';
const NFC_ID_STORAGE_KEY = 'gs_nfc_id';
const NFC_SHOP_CHAT_KEY = 'gs_nfc_shop_chat';
const NFC_ENTRY_FLAG = 'gs_nfc_entry';

// BACKEND_URL inclut déjà /api (cf. App.js)
const BACKEND_URL =
  (typeof process !== 'undefined' && (process.env?.REACT_APP_API_URL || process.env?.REACT_APP_BACKEND_URL))
  || 'https://global-strains.shop/api';

function bufToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return typeof btoa === 'function' ? btoa(binary) : Buffer.from(bytes).toString('base64');
}

function spkiToPem(spkiBuf) {
  const b64 = bufToBase64(spkiBuf);
  const lines = b64.match(/.{1,64}/g) || [];
  return `-----BEGIN PUBLIC KEY-----\n${lines.join('\n')}\n-----END PUBLIC KEY-----`;
}

async function generateKeyPair() {
  const pair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['sign', 'verify']
  );
  return pair;
}

async function exportPrivateJwk(privateKey) {
  return crypto.subtle.exportKey('jwk', privateKey);
}

async function exportPublicPem(publicKey) {
  const spki = await crypto.subtle.exportKey('spki', publicKey);
  return spkiToPem(spki);
}

async function importPrivateJwk(jwk) {
  return crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['sign']
  );
}

/**
 * Récupère la paire de clés existante ou en génère une nouvelle.
 * La clé privée reste toujours sur l'appareil (localStorage).
 * @returns {Promise<{ privateKey: CryptoKey, publicKeyPem: string }>}
 */
export async function getOrCreateKeyPair() {
  try {
    const storedJwk = localStorage.getItem(PRIVATE_KEY_STORAGE_KEY);
    const storedPem = localStorage.getItem(PUBLIC_KEY_STORAGE_KEY);
    if (storedJwk && storedPem) {
      const jwk = JSON.parse(storedJwk);
      const privateKey = await importPrivateJwk(jwk);
      return { privateKey, publicKeyPem: storedPem };
    }
  } catch (e) {
    console.warn('⚠️ Impossible de charger la clé existante, régénération:', e?.message);
  }

  const pair = await generateKeyPair();
  const jwk = await exportPrivateJwk(pair.privateKey);
  const publicKeyPem = await exportPublicPem(pair.publicKey);
  try {
    localStorage.setItem(PRIVATE_KEY_STORAGE_KEY, JSON.stringify(jwk));
    localStorage.setItem(PUBLIC_KEY_STORAGE_KEY, publicKeyPem);
  } catch (e) {
    console.error('❌ Impossible de persister la clé de licence:', e?.message);
  }
  return { privateKey: pair.privateKey, publicKeyPem };
}

export function hasStoredLicenseKey() {
  try {
    return !!(localStorage.getItem(PRIVATE_KEY_STORAGE_KEY) && localStorage.getItem(PUBLIC_KEY_STORAGE_KEY));
  } catch {
    return false;
  }
}

/**
 * Signe un challenge (chaîne UTF-8) avec la clé privée ECDSA P-256.
 * @param {CryptoKey} privateKey
 * @param {string} challenge
 * @returns {Promise<string>} signature en base64 (IEEE P1363 raw r||s)
 */
export async function signChallenge(privateKey, challenge) {
  const data = new TextEncoder().encode(String(challenge));
  const sigBuf = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    privateKey,
    data
  );
  return bufToBase64(sigBuf);
}

/**
 * Enregistre la clé publique de l'appareil côté serveur.
 * Nécessite une auth existante (initData/pwaToken/deviceId/cookie) pour connaître le chatId.
 * @param {string} publicKeyPem
 * @param {string} [deviceId]
 * @returns {Promise<{success:boolean, status?:string, error?:string, code?:string}>}
 */
export async function registerLicense(publicKeyPem, deviceId) {
  try {
    const res = await fetch(`${BACKEND_URL}/license/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ publicKey: publicKeyPem, deviceId: deviceId || null }),
    });
    const data = await res.json();
    return data;
  } catch (e) {
    return { success: false, error: e?.message || 'Erreur réseau' };
  }
}

/**
 * Authentifie l'appareil par challenge-response.
 * À utiliser lors des visites NFC/signet (sans initData Telegram).
 * @param {string} chatId
 * @returns {Promise<{success:boolean, chatId?:string, error?:string}>}
 */
export async function verifyAndAuthenticate(chatId) {
  try {
    const cid = String(chatId || '').trim();
    if (!cid) return { success: false, error: 'chatId manquant' };
    if (!hasStoredLicenseKey()) return { success: false, error: 'NO_LICENSE_KEY' };

    const { privateKey } = await getOrCreateKeyPair();

    const chalRes = await fetch(`${BACKEND_URL}/license/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ chatId: cid }),
    });
    const chalData = await chalRes.json();
    if (!chalData?.success || !chalData.challenge) {
      return { success: false, error: chalData?.error || 'Échec challenge' };
    }

    const signature = await signChallenge(privateKey, chalData.challenge);

    const verifyRes = await fetch(`${BACKEND_URL}/license/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ chatId: cid, challenge: chalData.challenge, signature }),
    });
    const verifyData = await verifyRes.json();
    return verifyData;
  } catch (e) {
    return { success: false, error: e?.message || 'Erreur réseau' };
  }
}

/**
 * Récupère le statut de la licence (auth requise).
 * @returns {Promise<{success:boolean, hasLicense?:boolean, license?:object}>}
 */
export async function getLicenseStatus() {
  try {
    const res = await fetch(`${BACKEND_URL}/license/status`, {
      credentials: 'include',
    });
    return await res.json();
  } catch (e) {
    return { success: false, error: e?.message || 'Erreur réseau' };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// NFC physique — identité locale (sans Telegram)
// ═══════════════════════════════════════════════════════════════════════════

export function markNfcEntry() {
  try { sessionStorage.setItem(NFC_ENTRY_FLAG, '1'); } catch { /* */ }
}

export function clearNfcEntryFlag() {
  try { sessionStorage.removeItem(NFC_ENTRY_FLAG); } catch { /* */ }
}

export function isNfcEntryActive() {
  try {
    if (sessionStorage.getItem(NFC_ENTRY_FLAG) === '1') return true;
  } catch { /* */ }
  try {
    return new URLSearchParams(window.location.search).get('src') === 'nfc';
  } catch {
    return false;
  }
}

/** UUID v4 simple (crypto.getRandomValues) */
function uuidV4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function getStoredNfcId() {
  try {
    const id = localStorage.getItem(NFC_ID_STORAGE_KEY);
    if (id && /^nfc_[0-9a-f-]{36}$/i.test(id)) return id;
  } catch { /* */ }
  return null;
}

export function getOrCreateNfcId() {
  let id = getStoredNfcId();
  if (id) return id;
  id = `nfc_${uuidV4()}`;
  try { localStorage.setItem(NFC_ID_STORAGE_KEY, id); } catch { /* */ }
  return id;
}

/**
 * Session cookie gs_nfc encore valide ?
 */
function persistNfcShopChatId(chatId) {
  const cid = String(chatId || '').trim();
  if (!cid) return;
  try {
    localStorage.setItem(NFC_SHOP_CHAT_KEY, cid);
    localStorage.setItem('telegramChatId', cid);
  } catch { /* */ }
}

export function getStoredNfcShopChatId() {
  try {
    return localStorage.getItem(NFC_SHOP_CHAT_KEY) || null;
  } catch {
    return null;
  }
}

export async function checkNfcSession() {
  try {
    const res = await fetch(`${BACKEND_URL}/nfc/session`, { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.success && data.nfcId) {
      try { localStorage.setItem(NFC_ID_STORAGE_KEY, data.nfcId); } catch { /* */ }
      const shopChatId = data.chatId || data.shopChatId || data.client?.chatId;
      if (shopChatId) persistNfcShopChatId(shopChatId);
      return { ...data, chatId: shopChatId || null };
    }
    return { success: false, error: data?.error || 'NO_SESSION' };
  } catch (e) {
    return { success: false, error: e?.message || 'Erreur réseau' };
  }
}

/**
 * Auth NFC complète : register clé → challenge → verify → cookie gs_nfc.
 */
export async function ensureNfcAuth() {
  try {
    const nfcId = getOrCreateNfcId();
    const { privateKey, publicKeyPem } = await getOrCreateKeyPair();

    const reg = await fetch(`${BACKEND_URL}/nfc/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ nfcId, publicKey: publicKeyPem }),
    });
    const regData = await reg.json().catch(() => ({}));
    if (!regData?.success) {
      return {
        success: false,
        error: regData?.error || 'Échec register NFC',
        code: regData?.code,
      };
    }

    const chalRes = await fetch(`${BACKEND_URL}/nfc/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ nfcId }),
    });
    const chalData = await chalRes.json().catch(() => ({}));
    if (!chalData?.success || !chalData.challenge) {
      return { success: false, error: chalData?.error || 'Échec challenge NFC' };
    }

    const signature = await signChallenge(privateKey, chalData.challenge);
    const verifyRes = await fetch(`${BACKEND_URL}/nfc/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        nfcId,
        challenge: chalData.challenge,
        signature,
      }),
    });
    const verifyData = await verifyRes.json().catch(() => ({}));
    if (!verifyRes.ok || !verifyData?.success) {
      return { success: false, error: verifyData?.error || 'Échec verify NFC' };
    }
    const shopChatId = verifyData.chatId || verifyData.shopChatId || verifyData.client?.chatId;
    if (shopChatId) persistNfcShopChatId(shopChatId);
    return {
      success: true,
      nfcId: verifyData.nfcId || nfcId,
      chatId: shopChatId || null,
      shopChatId: shopChatId || null,
      isNew: !!verifyData.isNew,
      client: verifyData.client || null,
    };
  } catch (e) {
    return { success: false, error: e?.message || 'Erreur réseau' };
  }
}

/** Compte un scan tag (unique / jour côté serveur) */
export async function trackNfcHit(nfcId) {
  try {
    await fetch(`${BACKEND_URL}/nfc/hit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ nfcId: nfcId || getStoredNfcId() || '' }),
    });
  } catch { /* soft */ }
}
